import React from "react";
import type { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { ActionChip } from "./components/ActionChip";
import { SourceChip } from "./components/SourceChip";
import {
  formatAuditDateAbsolute,
  formatAuditDateRelative,
} from "./utils/dateFormatting";
import type { AuditLog } from "./types";

interface MakeColumnsOptions {
  /** Invoked when the user clicks the "View" action on a row. */
  onView: (row: AuditLog) => void;
}

/**
 * Pretty resource type for the table cell. The backend uses snake_case
 * identifiers (`bgp_neighbor`, `propagation_rule`) which read poorly in
 * a dense table — convert to "BGP neighbor", "Propagation rule".
 *
 * Acronyms get special-cased so we don't ship "Bgp neighbor".
 */
const ACRONYMS = new Set([
  "bgp",
  "bbaas",
  "vrf",
  "dci",
  "fwaas",
  "sdwan",
  "ip",
]);

export function humanizeResourceType(value: string | null | undefined): string {
  if (!value) return "—";
  return value
    .split("_")
    .map((part, idx) => {
      if (ACRONYMS.has(part)) return part.toUpperCase();
      if (idx === 0) {
        return part.charAt(0).toUpperCase() + part.slice(1);
      }
      return part;
    })
    .join(" ");
}

export function makeAuditLogColumns({
  onView,
}: MakeColumnsOptions): GenericTableColumn<AuditLog>[] {
  return [
    {
      id: "createdAt",
      header: "Date",
      accessor: "createdAt",
      sortAccessor: (row) => row.createdAt ?? "",
      width: "180px",
      wrapMode: "nowrap",
      renderCell: (row) => (
        <span title={formatAuditDateAbsolute(row.createdAt)}>
          {formatAuditDateRelative(row.createdAt)}
        </span>
      ),
    },
    {
      id: "action",
      header: "Action",
      sortAccessor: (row) => row.action,
      width: "140px",
      wrapMode: "nowrap",
      renderCell: (row) => (
        <div className="flex items-center gap-2">
          <ActionChip action={row.action} />
        </div>
      ),
    },
    {
      id: "resource",
      header: "Resource",
      sortAccessor: (row) =>
        `${row.resourceType ?? ""} ${row.resourceName ?? ""}`,
      renderCell: (row) => (
        <div className="flex flex-col min-w-0">
          <span
            className="text-sm font-medium text-semantic-text-primary truncate"
            title={row.resourceName ?? undefined}
          >
            {row.resourceName ?? `#${row.resourceId}`}
          </span>
          <span className="text-xs text-semantic-text-secondary truncate">
            {humanizeResourceType(row.resourceType)} · ID {row.resourceId}
          </span>
        </div>
      ),
    },
    {
      id: "product",
      header: "Product",
      sortAccessor: (row) => row.product?.name ?? "",
      width: "160px",
      wrapMode: "nowrap",
      renderCell: (row) => (
        <span className="text-sm text-semantic-text-primary">
          {row.product?.name ?? "—"}
        </span>
      ),
    },
    {
      id: "user",
      header: "User",
      sortAccessor: (row) => row.userEmail ?? "",
      renderCell: (row) => (
        <span
          className="text-sm text-semantic-text-primary truncate block"
          title={row.userEmail}
        >
          {row.userEmail || "—"}
        </span>
      ),
    },
    {
      id: "source",
      header: "Source",
      sortAccessor: (row) => row.source,
      width: "130px",
      wrapMode: "nowrap",
      renderCell: (row) => <SourceChip source={row.source} />,
    },
    {
      id: "view",
      header: "",
      width: "80px",
      wrapMode: "nowrap",
      renderCell: (row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onView(row);
          }}
          className="text-semantic-text-brand text-sm font-medium hover:underline focus:underline"
        >
          View
        </button>
      ),
    },
  ];
}
